import React, { useState, useContext, useEffect, useRef } from 'react';
import { toast } from '../../lib/toast';
import { LayoutGrid, Plus, RotateCcw, List, Search, Power, X, Wifi, WifiOff, ChevronDown, Check } from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { cn } from '../../lib/utils';
import { getDeviceUIModel, sortLocations, sortFloors } from '../../lib/deviceUiModel';
import { SegmentedControl } from '../ui/segmented-control';

import DeviceAdder from './DeviceAdder';
import BulkDeviceManager from './BulkDeviceManager';
import DeviceTile from './DeviceTile';
import DeviceDetailsSheet from './DeviceDetailsSheet';
import PageHeader from '../ui/page-header';


const GROUP_BY_STORAGE_KEY = 'deviceGroupBy';
const GROUP_BY_OPTIONS = [
  { label: 'Rooms', value: 'location' },
  { label: 'Floors', value: 'floor' },
  { label: 'Types', value: 'type' },
];

const TYPE_ORDER = ['light', 'switch', 'thermostat', 'curtain', 'lock', 'sensor', 'speaker'];

const TYPE_LABELS = {
  light: 'Lights',
  switch: 'Switches',
  thermostat: 'Thermostats',
  curtain: 'Curtains',
  lock: 'Locks',
  sensor: 'Sensors',
  speaker: 'Speakers',
  other: 'Other',
};

const NO_FLOOR_KEYS = ['unassigned', 'na', 'n/a', 'not set'];

const DeviceManager = () => {
  const { devices, controlDevice, deleteDevice, refreshDevices, config, isConnected } = useContext(AppContext);
  const { isAdmin } = useAuth();
  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const [showBulkManager, setShowBulkManager] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyOn, setShowOnlyOn] = useState(false);
  const [groupBy, setGroupBy] = useState(() => {
    const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY);
    return GROUP_BY_OPTIONS.some(opt => opt.value === stored) ? stored : 'location';
  });
  // Header collapse-on-scroll: the full home header + controls live at the top;
  // once scrolled past, they condense into a slim pinned bar that keeps
  // search / On / grouping reachable at any depth.
  const [collapsed, setCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const groupMenuRef = useRef(null);
  // Mirror searchOpen into a ref so the scroll listener can read it without
  // re-subscribing — used to keep the header from collapsing away mid-search.
  const searchOpenRef = useRef(false);
  useEffect(() => { searchOpenRef.current = searchOpen; }, [searchOpen]);

  useEffect(() => {
    if (devices) {
      setLoading(false);
    }
  }, [devices]);

  // Hysteresis (collapse at 130px, expand below 70px) so the swap between the
  // full header and the slim bar can't flicker around a single threshold.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setCollapsed(prev => {
        // While the collapsed search is open, hold the header state — a short
        // filtered list can clamp scrollY and would otherwise unmount the input
        // and drop focus mid-keystroke.
        if (searchOpenRef.current) return prev;
        const next = prev ? y > 70 : y > 130;
        // Leaving the collapsed bar — drop its transient search/menu state so
        // it re-opens clean next time.
        if (prev && !next) {
          setSearchOpen(false);
          setGroupMenuOpen(false);
        }
        return next;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the collapsed group-by menu on outside click.
  useEffect(() => {
    if (!groupMenuOpen) return;
    const onDown = (e) => {
      if (groupMenuRef.current && !groupMenuRef.current.contains(e.target)) {
        setGroupMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [groupMenuOpen]);

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    localStorage.setItem(GROUP_BY_STORAGE_KEY, value);
    // Content reorganizes — a stale deep scroll offset is meaningless, so
    // return to the top of the newly-grouped list.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const homeName = config?.system?.name || 'Mirfa Home';
  const statusNode = (
    <span className="inline-flex items-center gap-1.5">
      {isConnected ? (
        <>
          <Wifi className="h-3.5 w-3.5 text-green-500" />
          <span>Online</span>
          <span className="text-border">•</span>
          <span>Local Control</span>
        </>
      ) : (
        <>
          <WifiOff className="h-3.5 w-3.5 text-destructive" />
          <span>Offline</span>
          <span className="text-border">•</span>
          <span>Checking connection...</span>
        </>
      )}
    </span>
  );

  // Apply the search query (name / location / floor) then the optional
  // "only ON" narrowing. onCount is computed from the search result so the
  // badge reflects "how many of what you're looking at are on".
  const query = searchQuery.toLowerCase().trim();
  const searchFiltered = query
    ? (devices || []).filter(device => {
        const name = (device.name || device.id || '').toLowerCase();
        const location = (device.location || '').toLowerCase();
        const floor = (device.floor || '').toLowerCase();
        return name.includes(query) || location.includes(query) || floor.includes(query);
      })
    : (devices || []);

  const onCount = searchFiltered.filter(device => getDeviceUIModel(device).isOn).length;
  const visibleDevices = showOnlyOn
    ? searchFiltered.filter(device => getDeviceUIModel(device).isOn)
    : searchFiltered;

  const hasDevices = (devices?.length || 0) > 0;

  // Bucket key per grouping mode. No-floor variants collapse into a single
  // "unassigned" bucket so they render as one "No Floor" section.
  const getGroupKey = (device) => {
    if (groupBy === 'floor') {
      const floor = (device.floor || 'unassigned').toLowerCase();
      return NO_FLOOR_KEYS.includes(floor) ? 'unassigned' : floor;
    }
    if (groupBy === 'type') {
      return (device.type || 'other').toLowerCase();
    }
    return (device.location || 'unknown').toLowerCase();
  };

  const sortGroupKeys = (keys) => {
    if (groupBy === 'floor') {
      const sorted = sortFloors(keys.filter(key => key !== 'unassigned'));
      return keys.includes('unassigned') ? [...sorted, 'unassigned'] : sorted;
    }
    if (groupBy === 'type') {
      return [...keys].sort((a, b) => {
        const aIndex = TYPE_ORDER.indexOf(a);
        const bIndex = TYPE_ORDER.indexOf(b);
        if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }
    return sortLocations(keys);
  };

  const getGroupedDevices = () => {
    const groupMap = new Map();

    visibleDevices.forEach(device => {
      const key = getGroupKey(device);
      if (!groupMap.has(key)) {
        groupMap.set(key, []);
      }
      groupMap.get(key).push(device);
    });

    return sortGroupKeys(Array.from(groupMap.keys())).map(key => [key, groupMap.get(key)]);
  };

  const groupedDevices = getGroupedDevices();

  const handleAddDevice = () => {
    setIsAddingDevice(true);
  };

  const handleCloseDeviceAdder = () => {
    setIsAddingDevice(false);
  };

  const handleDeviceAdded = async () => {
    try {
      await refreshDevices();
    } catch (error) {
      console.error('Error refreshing devices:', error);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      await refreshDevices();
    } catch (error) {
      console.error('Error refreshing devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTileClick = (device) => {
    setSelectedDevice(device);
    setShowDetailsSheet(true);
  };

  const handleControlDevice = async (deviceId, property, value, parentId) => {
    try {
      await controlDevice(deviceId, property, value, parentId);
    } catch (error) {
      // controlDevice already surfaced the toast; just keep the trace.
      console.error(`Error setting ${property}:`, error);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      await deleteDevice(deviceId);
      // toast.success('Device deleted successfully');
    } catch (error) {
      console.error('Error deleting device:', error);
      toast.error('Failed to delete device');
    }
  };

  const renderEmptyState = () => (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 px-4">
        <div className="text-muted-foreground mb-4">
          <LayoutGrid className="w-12 h-12" />
        </div>
        <p className="text-lg mb-6 text-center">No devices found.</p>
        <Button
          onClick={handleAddDevice}
          disabled={!isAdmin()}
          title={!isAdmin() ? 'Admin access required' : ''}
        >
          <Plus className="mr-2" /> Add Your First Device
        </Button>
      </CardContent>
    </Card>
  );

  const formatTitleCase = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatSectionTitle = (key) => {
    if (groupBy === 'floor') {
      if (key === 'unassigned') return 'No Floor';
      return key.includes('floor') ? formatTitleCase(key) : `Floor ${formatTitleCase(key)}`;
    }
    if (groupBy === 'type') {
      return TYPE_LABELS[key] || formatTitleCase(key);
    }
    return formatTitleCase(key);
  };

  const renderSection = (sectionKey, sectionDevices) => {
    return (
      <div key={sectionKey} className="mb-8">
        <h2 className="sticky top-[52px] z-10 text-sm font-medium text-muted-foreground mb-3 -mx-4 px-5 py-2 bg-background/90 backdrop-blur-sm">
          {formatSectionTitle(sectionKey)}
        </h2>
        <div className="grid grid-cols-2 gap-3">

          {sectionDevices.map(device => (
            <DeviceTile
              key={device.id}
              device={device}
              onControl={handleControlDevice}
              onClick={() => handleTileClick(device)}
              loading={loading}
            />
          ))}
        </div>
      </div>
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setShowOnlyOn(false);
  };

  const renderNoMatches = () => (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="text-muted-foreground mb-3">
        <Search className="w-10 h-10" />
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {showOnlyOn && query
          ? 'No devices that are on match your search.'
          : showOnlyOn
            ? 'No devices are currently on.'
            : 'No devices match your search.'}
      </p>
      <Button variant="secondary" size="sm" onClick={clearFilters}>
        Clear filters
      </Button>
    </div>
  );

  const renderDeviceGrid = () => {
    if (groupedDevices.length === 0) {
      // No devices at all vs. filters excluding everything.
      return hasDevices ? renderNoMatches() : renderEmptyState();
    }

    return (
      <div className="space-y-0">
        {groupedDevices.map(([sectionKey, sectionDevices]) =>
          renderSection(sectionKey, sectionDevices)
        )}
      </div>
    );
  };

  if (loading && devices.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading devices...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showBulkManager) {
    return (
      <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Bulk Device Manager</h1>
          <Button
            variant="secondary"
            onClick={() => setShowBulkManager(false)}
          >
            Back to Devices
          </Button>
        </div>
        <BulkDeviceManager />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-0 md:py-0">
      {collapsed && hasDevices ? (
        <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/40">
          {searchOpen ? (
            <div className="flex items-center gap-2 px-4 min-h-[52px] py-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  autoFocus
                  type="text"
                  inputMode="search"
                  placeholder="Search devices"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 rounded-xl bg-muted/50 border border-border/60 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                className="text-sm font-medium text-primary px-1 shrink-0"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 min-h-[52px] py-2">
              <h1 className="text-base font-semibold truncate min-w-0 flex-1">{homeName}</h1>
              <button
                type="button"
                aria-label="Search devices"
                onClick={() => setSearchOpen(true)}
                className={cn(
                  'h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-colors',
                  query.length > 0
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/60'
                )}
              >
                <Search className="w-[18px] h-[18px]" />
              </button>
              <button
                type="button"
                onClick={() => setShowOnlyOn((prev) => !prev)}
                aria-pressed={showOnlyOn}
                className={cn(
                  'flex items-center gap-1 h-9 px-2.5 rounded-full border text-sm font-medium shrink-0 transition-colors',
                  showOnlyOn
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/50 text-foreground border-border/60 hover:bg-muted'
                )}
              >
                <Power className="w-3.5 h-3.5" />
                {onCount}
              </button>
              <div className="relative shrink-0" ref={groupMenuRef}>
                <button
                  type="button"
                  onClick={() => setGroupMenuOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={groupMenuOpen}
                  className="flex items-center gap-1 h-9 px-3 rounded-full border border-border/60 bg-muted/50 text-sm font-medium hover:bg-muted transition-colors"
                >
                  {GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {groupMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-background border border-border rounded-xl shadow-lg py-1 z-30 animate-in fade-in-0 zoom-in-95 duration-100">
                    {GROUP_BY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { handleGroupByChange(opt.value); setGroupMenuOpen(false); }}
                        className={cn(
                          'w-full px-4 py-2.5 text-left text-sm flex items-center justify-between hover:bg-accent transition-colors',
                          groupBy === opt.value && 'text-primary font-medium'
                        )}
                      >
                        {opt.label}
                        {groupBy === opt.value && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <PageHeader
          title={homeName}
          subtitle={statusNode}
          onAdd={isAdmin() ? handleAddDevice : undefined}
          addDisabled={loading}
          showAdd={isAdmin()}
          menuItems={[
            { label: 'Bulk Manage', icon: List, onClick: () => setShowBulkManager(true) },
            { label: 'Refresh', icon: RotateCcw, onClick: handleRefresh }
          ]}
        />
      )}

      {hasDevices && (
        <div className="px-4 pt-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                inputMode="search"
                placeholder="Search devices"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-9 pr-9 rounded-xl bg-muted/50 border border-border/60 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
              {query.length > 0 && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowOnlyOn((prev) => !prev)}
              aria-pressed={showOnlyOn}
              className={cn(
                'flex items-center gap-1.5 h-10 px-3.5 rounded-xl border text-sm font-medium transition-colors',
                showOnlyOn
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/50 text-foreground border-border/60 hover:bg-muted'
              )}
            >
              <Power className="w-4 h-4" />
              On
              <span
                className={cn(
                  'ml-0.5 min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[11px] font-semibold text-center leading-none',
                  showOnlyOn
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-background text-muted-foreground'
                )}
              >
                {onCount}
              </span>
            </button>
          </div>
          <SegmentedControl
            className="mt-3"
            options={GROUP_BY_OPTIONS}
            value={groupBy}
            onChange={handleGroupByChange}
          />
        </div>
      )}

      <div className="px-4 py-6">
        {renderDeviceGrid()}
      </div>

      {isAddingDevice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <DeviceAdder
              onClose={handleCloseDeviceAdder}
              onDeviceAdded={handleDeviceAdded}
            />
          </div>
        </div>
      )}

      {selectedDevice && (
        <DeviceDetailsSheet
          deviceId={selectedDevice.id}
          devices={devices}
          open={showDetailsSheet}
          onClose={() => {
            setShowDetailsSheet(false);
            setSelectedDevice(null);
          }}
          onControl={handleControlDevice}
          onDelete={handleDeleteDevice}
          loading={loading}
        />
      )}
    </div>
  );
};

export default DeviceManager;
